import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { EntraAuthProvider, useEntraAuth, EntraConfigChecker } from './components/EntraAuth';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import LoginEntra from './LoginEntra';
import Patients from './pages/Patients';
import Appointments from './pages/Appointments';
import Doctors from './pages/Doctors';
import Staff from './pages/Staff';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useEntraAuth();

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Se încarcă...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/" replace />;
};

// Intranet Component
const Intranet = () => {
  const { user, logout } = useEntraAuth();

  return (
    <div className="intranet-container">
      <header className="intranet-header">
        <div className="header-content">
          <div className="logo-section">
            <h1>Evoo Medical Clinic</h1>
            <p>Portal Intern de Management</p>
          </div>
          <div className="user-section">
            <div className="user-info">
              <span className="welcome-text">Bine ai venit, {user?.name || 'User'}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button onClick={logout} className="logout-btn">Deconectare</button>
          </div>
        </div>
      </header>

      <nav className="intranet-nav">
        <div className="nav-content">
          <ul className="nav-menu">
            <li><NavLink to="/dashboard" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Dashboard</NavLink></li>
            <li><NavLink to="/patients" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Pacienți</NavLink></li>
            <li><NavLink to="/appointments" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Programări</NavLink></li>
            <li><NavLink to="/doctors" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Doctori</NavLink></li>
            <li><NavLink to="/staff" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Personal</NavLink></li>
            <li><NavLink to="/reports" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Rapoarte</NavLink></li>
            <li><NavLink to="/settings" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Setări</NavLink></li>
          </ul>
        </div>
      </nav>

      <main className="intranet-main">
        <div className="main-content">
          <section id="dashboard" className="dashboard-section">
            <h2>Prezentare Generală</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Patients</h3>
                <div className="stat-number">1,247</div>
                <div className="stat-change positive">+12% this month</div>
              </div>
              <div className="stat-card">
                <h3>Today's Appointments</h3>
                <div className="stat-number">28</div>
                <div className="stat-change">5 remaining</div>
              </div>
              <div className="stat-card">
                <h3>Active Doctors</h3>
                <div className="stat-number">15</div>
                <div className="stat-change">All on duty</div>
              </div>
              <div className="stat-card">
                <h3>Monthly Revenue</h3>
                <div className="stat-number">€45,230</div>
                <div className="stat-change positive">+8% from last month</div>
              </div>
            </div>

            <div className="dashboard-widgets">
              <div className="widget">
                <h3>Recent Appointments</h3>
                <div className="appointment-list">
                  <div className="appointment-item">
                    <div className="appointment-time">09:00</div>
                    <div className="appointment-details">
                      <div className="patient-name">Maria Popescu</div>
                      <div className="doctor-name">Dr. Ioan Dumitrescu</div>
                    </div>
                  </div>
                  <div className="appointment-item">
                    <div className="appointment-time">09:30</div>
                    <div className="appointment-details">
                      <div className="patient-name">Andrei Vasile</div>
                      <div className="doctor-name">Dr. Elena Marinescu</div>
                    </div>
                  </div>
                  <div className="appointment-item">
                    <div className="appointment-time">10:00</div>
                    <div className="appointment-details">
                      <div className="patient-name">Ioana Radu</div>
                      <div className="doctor-name">Dr. Mihai Constantinescu</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="widget">
                <h3>Clinic Information</h3>
                <div className="clinic-info">
                  <div className="info-item">
                    <strong>Address:</strong> Strada Clinicilor 1, Bucharest, Romania
                  </div>
                  <div className="info-item">
                    <strong>Phone:</strong> +40 21 123 4567
                  </div>
                  <div className="info-item">
                    <strong>Email:</strong> contact@evoomedical.ro
                  </div>
                  <div className="info-item">
                    <strong>Working Hours:</strong> Mon-Fri 8:00-20:00, Sat 9:00-14:00
                  </div>
                  <div className="info-item">
                    <strong>Emergency:</strong> +40 21 999 0000
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="announcements" className="announcements-section">
            <h2>Announcements & Updates</h2>
            <div className="announcement-list">
              <div className="announcement-item">
                <div className="announcement-date">2025-01-15</div>
                <div className="announcement-content">
                  <h4>New Medical Equipment Installed</h4>
                  <p>The clinic has received new state-of-the-art MRI equipment. Training sessions will be held next week.</p>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-date">2025-01-10</div>
                <div className="announcement-content">
                  <h4>Staff Meeting</h4>
                  <p>Mandatory staff meeting on January 20th at 5:00 PM in the main conference room.</p>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-date">2025-01-05</div>
                <div className="announcement-content">
                  <h4>Holiday Schedule Update</h4>
                  <p>Clinic will be closed on January 25th for the national holiday. Emergency services available.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="intranet-footer">
        <div className="footer-content">
          <p>&copy; 2025 Evoo Medical. Toate drepturile rezervate.</p>
          <div className="footer-links">
            <a href="#privacy">Politica de Confidențialitate</a>
            <a href="#terms">Termeni și Condiții</a>
            <a href="#support">Suport</a>
          </div>
        </div>
      </footer>

      <style jsx>{`
        .intranet-container {
          min-height: 100vh;
          background: #f5f5f5;
        }

        .intranet-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1rem 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo-section h1 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .logo-section p {
          margin: 0;
          font-size: 0.9rem;
          opacity: 0.9;
        }

        .user-section {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .user-info {
          text-align: right;
        }

        .welcome-text {
          display: block;
          font-weight: 600;
        }

        .user-email {
          display: block;
          font-size: 0.8rem;
          opacity: 0.8;
        }

        .logout-btn {
          padding: 0.5rem 1rem;
          background: rgba(255,255,255,0.2);
          color: white;
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.3s;
        }

        .logout-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .intranet-nav {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }

        .nav-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .nav-menu {
          display: flex;
          list-style: none;
          margin: 0;
          padding: 0;
          gap: 2rem;
        }

        .nav-link {
          display: block;
          padding: 1rem 0;
          color: #666;
          text-decoration: none;
          font-weight: 500;
          border-bottom: 2px solid transparent;
          transition: all 0.3s;
        }

        .nav-link:hover, .nav-link.active {
          color: #667eea;
          border-bottom-color: #667eea;
        }

        .intranet-main {
          flex: 1;
          padding: 2rem 0;
        }

        .main-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .dashboard-section h2 {
          color: #333;
          margin-bottom: 2rem;
          font-size: 2rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 3rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          border-left: 4px solid #667eea;
        }

        .stat-card h3 {
          margin: 0 0 0.5rem 0;
          color: #666;
          font-size: 0.9rem;
          text-transform: uppercase;
          font-weight: 600;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: bold;
          color: #333;
          margin-bottom: 0.5rem;
        }

        .stat-change {
          font-size: 0.8rem;
          color: #666;
        }

        .stat-change.positive {
          color: #28a745;
        }

        .dashboard-widgets {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 2rem;
        }

        .widget {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .widget h3 {
          margin: 0 0 1rem 0;
          color: #333;
          font-size: 1.2rem;
        }

        .appointment-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .appointment-item {
          display: flex;
          align-items: center;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #667eea;
        }

        .appointment-time {
          font-weight: bold;
          color: #667eea;
          margin-right: 1rem;
          min-width: 60px;
        }

        .patient-name {
          font-weight: 600;
          color: #333;
        }

        .doctor-name {
          color: #666;
          font-size: 0.9rem;
        }

        .clinic-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .info-item {
          padding: 0.5rem 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .info-item:last-child {
          border-bottom: none;
        }

        .announcements-section {
          margin-top: 3rem;
        }

        .announcements-section h2 {
          color: #333;
          margin-bottom: 2rem;
        }

        .announcement-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .announcement-item {
          background: white;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          display: flex;
          gap: 1rem;
        }

        .announcement-date {
          font-weight: 600;
          color: #667eea;
          min-width: 100px;
        }

        .announcement-content h4 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }

        .announcement-content p {
          margin: 0;
          color: #666;
        }

        .intranet-footer {
          background: #333;
          color: white;
          padding: 2rem 0;
          margin-top: 4rem;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .footer-links {
          display: flex;
          gap: 2rem;
        }

        .footer-links a {
          color: #ccc;
          text-decoration: none;
          transition: color 0.3s;
        }

        .footer-links a:hover {
          color: white;
        }

        @media (max-width: 768px) {
          .header-content, .nav-content, .main-content, .footer-content {
            padding: 0 1rem;
          }

          .nav-menu {
            gap: 1rem;
            flex-wrap: wrap;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .dashboard-widgets {
            grid-template-columns: 1fr;
          }

          .footer-content {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

// App Content Component (inside provider context)
function AppContent() {
  // Enable inactivity logout for authenticated users
  useInactivityLogout();

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LoginEntra />} />
        <Route path="/login-entra" element={<LoginEntra />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Intranet />
          </ProtectedRoute>
        } />
        <Route path="/patients" element={
          <ProtectedRoute>
            <Patients />
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute>
            <Appointments />
          </ProtectedRoute>
        } />
        <Route path="/doctors" element={
          <ProtectedRoute>
            <Doctors />
          </ProtectedRoute>
        } />
        <Route path="/staff" element={
          <ProtectedRoute>
            <Staff />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Main App Component
function App() {
  return (
    <EntraAuthProvider>
      <EntraConfigChecker />
      <AppContent />
    </EntraAuthProvider>
  );
}

export default App;
